import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthProvider'
import { useMobileAttendance } from '@/hooks/useMobileAttendance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Loader2, Calendar, List, ChevronLeft, ChevronRight, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'

const statusColors = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  declined: 'bg-red-50 text-red-700 border-red-200',
  cancelled: 'bg-gray-50 text-gray-500 border-gray-200',
}

const statusDotColors = {
  pending: 'bg-amber-400',
  approved: 'bg-emerald-400',
  declined: 'bg-red-400',
  cancelled: 'bg-gray-400',
}

export default function AttendancePage() {
  const navigate = useNavigate()
  const { userProfile, company } = useAuth()
  const { leaveBalance, leaveRequests, loading } = useMobileAttendance(userProfile?.id, company?.id)
  const [viewMode, setViewMode] = useState('calendar')
  const [selectedDay, setSelectedDay] = useState(null)
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date()
    return new Date(now.getFullYear(), now.getMonth(), 1)
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
      </div>
    )
  }

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay()

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
    setSelectedDay(null)
  }
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
    setSelectedDay(null)
  }

  const getLeaveForDate = (day) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return leaveRequests.filter(lr => dateStr >= lr.start_date && dateStr <= lr.end_date)
  }

  const daysInMonth = getDaysInMonth(currentMonth)
  const firstDay = getFirstDayOfMonth(currentMonth)
  const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1

  const today = new Date()
  const isCurrentMonth = today.getFullYear() === currentMonth.getFullYear() && today.getMonth() === currentMonth.getMonth()

  return (
    <div className="p-4 space-y-3">
      {/* Header with title + request button */}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Leave</h1>
        <Button
          size="sm"
          className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs"
          onClick={() => navigate('/attendance/request')}
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Request
        </Button>
      </div>

      {/* Compact balance bar */}
      {leaveBalance && (
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-baseline gap-1.5">
            <span className="text-lg font-semibold text-gray-900">{leaveBalance.remaining_days ?? '-'}</span>
            <span className="text-xs text-gray-500">days remaining</span>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>{leaveBalance.taken_days ?? 0} taken</span>
            <span className="text-gray-200">|</span>
            <span>{leaveBalance.entitled_days ?? 0} entitled</span>
          </div>
        </div>
      )}

      {/* View toggle */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
        <button
          onClick={() => setViewMode('calendar')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition-colors',
            viewMode === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          )}
        >
          <Calendar className="h-3.5 w-3.5" />
          Calendar
        </button>
        <button
          onClick={() => setViewMode('list')}
          className={cn(
            'flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium rounded-md transition-colors',
            viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
          )}
        >
          <List className="h-3.5 w-3.5" />
          List
        </button>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          {/* Month navigation */}
          <div className="flex items-center justify-between mb-4">
            <button onClick={prevMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronLeft className="h-4 w-4 text-gray-600" />
            </button>
            <h2 className="text-sm font-semibold text-gray-900">
              {currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
            </h2>
            <button onClick={nextMonth} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <ChevronRight className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
              <div key={day} className="text-center text-[10px] font-medium text-gray-400 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {Array.from({ length: adjustedFirstDay }).map((_, i) => (
              <div key={`empty-${i}`} className="h-10" />
            ))}

            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const leaves = getLeaveForDate(day)
              const isToday = isCurrentMonth && day === today.getDate()
              const isWeekend = ((adjustedFirstDay + i) % 7) >= 5
              const isSelected = selectedDay === day
              const hasLeave = leaves.length > 0

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(isSelected ? null : day)}
                  className={cn(
                    'h-10 flex flex-col items-center justify-center relative rounded-lg transition-colors',
                    isSelected && 'bg-emerald-50',
                    !isSelected && hasLeave && 'hover:bg-gray-50'
                  )}
                >
                  <span className={cn(
                    'text-xs leading-none',
                    isToday && 'bg-emerald-600 text-white rounded-full w-6 h-6 flex items-center justify-center font-medium',
                    !isToday && isSelected && 'text-emerald-700 font-medium',
                    !isToday && !isSelected && !isWeekend && 'text-gray-700',
                    isWeekend && !isToday && !isSelected && 'text-gray-300'
                  )}>
                    {day}
                  </span>
                  {hasLeave && (
                    <div className="flex gap-0.5 mt-0.5">
                      {leaves.slice(0, 3).map((l, idx) => (
                        <div
                          key={idx}
                          className={cn('w-1 h-1 rounded-full', statusDotColors[l.status] || 'bg-gray-400')}
                        />
                      ))}
                    </div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
            {[['approved', 'Approved'], ['pending', 'Pending'], ['declined', 'Declined']].map(([key, label]) => (
              <div key={key} className="flex items-center gap-1">
                <div className={cn('w-2 h-2 rounded-full', statusDotColors[key])} />
                <span className="text-[10px] text-gray-500">{label}</span>
              </div>
            ))}
          </div>

          {/* Selected day detail */}
          {selectedDay && (() => {
            const leaves = getLeaveForDate(selectedDay)
            const selectedDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), selectedDay)
            const dateLabel = selectedDate.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })

            return (
              <div className="mt-3 pt-3 border-t border-gray-100">
                <p className="text-xs font-medium text-gray-900 mb-2">{dateLabel}</p>
                {leaves.length === 0 ? (
                  <p className="text-xs text-gray-400">No leave on this day.</p>
                ) : (
                  <div className="space-y-2">
                    {leaves.map(lr => (
                      <div key={lr.id} className="flex items-start justify-between gap-2 p-2.5 bg-gray-50 rounded-lg">
                        <div>
                          <div className="flex items-center gap-1.5">
                            <div className={cn('w-2 h-2 rounded-full', statusDotColors[lr.status] || 'bg-gray-400')} />
                            <p className="text-xs font-medium text-gray-900">{lr.leave_type}</p>
                          </div>
                          <p className="text-[11px] text-gray-500 mt-0.5 ml-3.5">
                            {new Date(lr.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            {' — '}
                            {new Date(lr.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            {lr.calculated_days && ` (${lr.calculated_days} day${lr.calculated_days !== 1 ? 's' : ''})`}
                          </p>
                          {lr.user_note && (
                            <p className="text-[11px] text-gray-400 mt-0.5 ml-3.5 italic">{lr.user_note}</p>
                          )}
                        </div>
                        <Badge variant="outline" className={cn('text-[10px] capitalize shrink-0', statusColors[lr.status] || statusColors.pending)}>
                          {lr.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })()}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-2">
          {leaveRequests.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
              <Calendar className="h-10 w-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">No leave requests found.</p>
            </div>
          ) : (
            leaveRequests.map(lr => {
              const colorClass = statusColors[lr.status] || statusColors.pending
              return (
                <div key={lr.id || lr.localId} className="bg-white rounded-xl border border-gray-200 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{lr.leave_type}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(lr.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                        {' — '}
                        {new Date(lr.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </p>
                      {lr.calculated_days && (
                        <p className="text-xs text-gray-400 mt-0.5">{lr.calculated_days} day{lr.calculated_days !== 1 ? 's' : ''}</p>
                      )}
                    </div>
                    <Badge variant="outline" className={cn('text-xs capitalize', colorClass)}>
                      {lr.status}
                    </Badge>
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
